import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import Spinner from 'react-spinkit';
import cx from 'classnames';
import { Icon } from '@iconify/react';
import styles from './CustomDropdown.module.scss';

interface Props {
  onSelectSubject: (_selectedId: string | null) => void;
  initialValue: string;
  currentlySelectedItem: string;
  disableChangeLocation: boolean;
  list: { id: string; name: string }[];
  icon?: string;
  noFilter?: boolean;
  placeholder?: string;
  className?: string;
}

const CustomDropdown = ({
  onSelectSubject,
  initialValue,
  currentlySelectedItem,
  disableChangeLocation,
  list,
  icon,
  noFilter,
  placeholder,
  className,
}: Props): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const [filterValue, setFilterValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [menuPlacement, setMenuPlacement] = useState<'top' | 'bottom'>('bottom');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const filterInputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxIdRef = useRef(`custom-dropdown-${Math.random().toString(36).slice(2, 9)}`);
  const listboxId = listboxIdRef.current;

  const hasExplicitSelection = Boolean(currentlySelectedItem);
  const selectedItemId = hasExplicitSelection ? currentlySelectedItem : null;
  const selectedItem =
    list.find((e) => e.id === (selectedItemId || '')) || list.find((e) => e.id === initialValue) || list[0];
  const initialObject = list.find((e) => e.id === initialValue);
  const initialDisplayValue = (initialObject && initialObject.name) || list[0]?.name || '';

  const visibleList = useMemo(() => {
    if (noFilter || !filterValue) {
      return list;
    }

    return list.filter((item) => item.name.toLowerCase().includes(filterValue.toLowerCase()));
  }, [filterValue, list, noFilter]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (noFilter) {
      return;
    }

    filterInputRef.current?.focus();
  }, [isOpen, noFilter]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setFilterValue('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (!initialValue) {
    return (
      <div className={styles.selectorErrorMessage}>
        <Spinner name='line-scale' />
      </div>
    );
  }

  const displayValue = hasExplicitSelection
    ? selectedItem?.name || initialDisplayValue
    : placeholder || selectedItem?.name || initialDisplayValue;

  const getDefaultHighlightedIndex = () => {
    if (visibleList.length === 0) {
      return -1;
    }

    if (!selectedItemId) {
      return -1;
    }

    const selectedIndex = visibleList.findIndex((item) => item.id === selectedItemId);
    return selectedIndex >= 0 ? selectedIndex : -1;
  };

  const openDropdown = () => {
    if (disableChangeLocation) {
      return;
    }

    if (triggerRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      const estimatedMenuHeight = noFilter ? 240 : 300;

      setMenuPlacement(spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow ? 'top' : 'bottom');
    }

    setIsOpen(true);
    setHighlightedIndex(getDefaultHighlightedIndex());
  };

  const closeDropdown = () => {
    setIsOpen(false);
    setFilterValue('');
    setHighlightedIndex(-1);
  };

  const selectItem = (itemId: string) => {
    onSelectSubject(itemId);
    closeDropdown();
    triggerRef.current?.focus();
  };

  const moveHighlight = (step: number) => {
    if (visibleList.length === 0) {
      return;
    }

    setHighlightedIndex((prevIndex) => {
      if (prevIndex < 0) {
        return step > 0 ? 0 : visibleList.length - 1;
      }

      const nextIndex = prevIndex + step;

      if (nextIndex < 0) {
        return visibleList.length - 1;
      }

      if (nextIndex >= visibleList.length) {
        return 0;
      }

      return nextIndex;
    });
  };

  const handleDropdownKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (disableChangeLocation) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          openDropdown();
          return;
        }
        moveHighlight(1);
        return;
      case 'ArrowUp':
        event.preventDefault();
        if (!isOpen) {
          openDropdown();
          return;
        }
        moveHighlight(-1);
        return;
      case 'Home':
        if (isOpen && visibleList.length > 0) {
          event.preventDefault();
          setHighlightedIndex(0);
        }
        return;
      case 'End':
        if (isOpen && visibleList.length > 0) {
          event.preventDefault();
          setHighlightedIndex(visibleList.length - 1);
        }
        return;
      case 'Enter':
        if (!isOpen) {
          event.preventDefault();
          openDropdown();
          return;
        }
        if (highlightedIndex >= 0 && visibleList[highlightedIndex]) {
          event.preventDefault();
          selectItem(visibleList[highlightedIndex].id);
        }
        return;
      case 'Escape':
        if (isOpen) {
          event.preventDefault();
          closeDropdown();
          triggerRef.current?.focus();
        }
        return;
      default:
        return;
    }
  };

  return (
    <div
      ref={dropdownRef}
      className={cx(styles.selecter, className, {
        [`${styles.disabledSelector}`]: disableChangeLocation,
      })}
    >
      <button
        type='button'
        ref={triggerRef}
        className={cx(styles.customToggle, {
          [`${styles.customToggleDisabled}`]: disableChangeLocation,
        })}
        onClick={() => {
          if (isOpen) {
            closeDropdown();
            return;
          }

          openDropdown();
        }}
        onKeyDown={handleDropdownKeyDown}
        disabled={disableChangeLocation}
        role='combobox'
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-haspopup='listbox'
        aria-activedescendant={
          isOpen && highlightedIndex >= 0 && visibleList[highlightedIndex]
            ? `${listboxId}-option-${visibleList[highlightedIndex].id}`
            : undefined
        }
      >
        {icon && <Icon className={styles.infoGraphicIcon} icon={icon} />}
        <span className={styles.selectedLabel}>{displayValue}</span>
        <Icon className={styles.arrowDownIcon} icon='bxs:down-arrow' />
      </button>

      {isOpen && (
        <div
          className={cx(styles.dropdownMenu, {
            [styles.dropdownMenuTop]: menuPlacement === 'top',
          })}
        >
          {!noFilter && (
            <input
              ref={filterInputRef}
              className={styles.formControl}
              placeholder='Type to filter...'
              onChange={(event) => {
                const value = event.target.value;
                setFilterValue(value);

                if (!value) {
                  setHighlightedIndex(getDefaultHighlightedIndex());
                  return;
                }

                setHighlightedIndex(0);
              }}
              onKeyDown={handleDropdownKeyDown}
              value={filterValue}
            />
          )}
          <ul id={listboxId} role='listbox' className={styles.optionsList} onKeyDown={handleDropdownKeyDown}>
            {visibleList.length === 0 ? (
              <li className={styles.emptyState}>No results found</li>
            ) : (
              visibleList.map((item, index) => {
                const isSelected = hasExplicitSelection && item.id === selectedItemId;
                const isActive = index === highlightedIndex;

                return (
                  <li
                    id={`${listboxId}-option-${item.id}`}
                    key={item.id}
                    role='option'
                    aria-selected={isSelected}
                    className={cx(styles.option, {
                      [`${styles.optionActive}`]: isActive,
                      [`${styles.optionSelected}`]: isSelected,
                    })}
                  >
                    <button
                      type='button'
                      className={styles.optionButton}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onClick={() => selectItem(item.id)}
                    >
                      {item.name}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CustomDropdown;
